/* eslint-disable class-methods-use-this */
import {
  Cell,
  CellTemplate,
  Compatible,
  Uncertain,
  UncertainCompatible,
  getCellProperty,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
} from '@silevis/reactgrid';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface StableTextCell extends Cell {
  type: 'text';
  text: string;
  originalText?: string;
  placeholder?: string;
  validator?: (text: string) => boolean;
  renderer?: (text: string) => React.ReactNode;
  errorMessage?: string;
  property?: string;
  rows?: number;
  colType?: string;
  format?: string;
  indicatorType?: string;
  isDraggable?: boolean;
  tableName?: string;
  hasFormulaError?: boolean;
  rowId?: string | number;
  columnId?: string;
}

interface StableTextEditorProperties {
  cell: Compatible<StableTextCell>;
  onCellChanged: (cell: Compatible<StableTextCell>, commit: boolean) => void;
  getCompatibleCell: (cell: Uncertain<StableTextCell>) => Compatible<StableTextCell>;
}

const StableTextEditor = ({
  cell,
  onCellChanged,
  getCompatibleCell,
}: StableTextEditorProperties) => {
  const [value, setValue] = useState(cell.text);
  const inputReference = useRef<HTMLInputElement | null>(null);
  const cancelEditReference = useRef(false);
  const commitEditReference = useRef(false);
  const originalText = cell.originalText ?? cell.text;

  const cancelValue = useCallback(
    (event?: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
      if (cancelEditReference.current) return;

      cancelEditReference.current = true;
      event?.preventDefault();
      onCellChanged(
        getCompatibleCell({
          ...cell,
          text: originalText,
        }),
        false,
      );
      window.requestAnimationFrame(() => inputReference.current?.blur());
    },
    [cell, getCompatibleCell, onCellChanged, originalText],
  );

  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.keyCode !== keyCodes.ESCAPE) return;
      if (document.activeElement !== inputReference.current) return;

      cancelValue(event);
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown, true);
  }, [cancelValue]);

  const commitValue = (nextValue: string) => {
    commitEditReference.current = true;
    onCellChanged(
      getCompatibleCell({
        ...cell,
        text: nextValue,
      }),
      true,
    );
  };

  return (
    <div style={{ width: '100%' }}>
      <input
        disabled={cell.nonEditable}
        style={{
          backgroundColor: '#FFFFFF',
          color: '#121826',
          width: '100%',
        }}
        ref={(input) => {
          inputReference.current = input;
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
        onBlur={() => {
          if (cancelEditReference.current || commitEditReference.current) return;
          commitValue(value);
        }}
        onCopy={(event) => event.stopPropagation()}
        onCut={(event) => event.stopPropagation()}
        onPaste={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        placeholder={cell.placeholder}
        onKeyDown={(event) => {
          if (event.key === 'Escape' || event.keyCode === keyCodes.ESCAPE) {
            cancelValue(event);
            return;
          }

          if (event.key === 'Enter' || event.keyCode === keyCodes.ENTER) {
            event.preventDefault();
            event.stopPropagation();
            commitValue(value);
            event.currentTarget.blur();
            return;
          }

          if (isAlphaNumericKey(event.keyCode) || isNavigationKey(event.keyCode)) {
            event.stopPropagation();
          }
        }}
      />
    </div>
  );
};

export class StableTextCellTemplate implements CellTemplate<StableTextCell> {
  onDeleteHeader?: (rowId: any) => void;

  constructor(onDeleteHeader?: (rowId: any) => void) {
    this.onDeleteHeader = onDeleteHeader;
  }

  getCompatibleCell(uncertainCell: Uncertain<StableTextCell>): Compatible<StableTextCell> {
    const text = getCellProperty(uncertainCell, 'text', 'string');
    let placeholder: string | undefined;
    try {
      placeholder = getCellProperty(uncertainCell, 'placeholder', 'string');
    } catch {
      placeholder = '';
    }
    const value = Number.parseFloat(text);
    return { ...uncertainCell, text, value, placeholder };
  }

  isFocusable?(cell: Compatible<any>): boolean {
    return cell?.property !== 'dimension';
  }

  update(
    cell: Compatible<StableTextCell>,
    cellToMerge: UncertainCompatible<StableTextCell>,
  ): Compatible<StableTextCell> {
    return this.getCompatibleCell({
      ...cell,
      text: cellToMerge.text,
      placeholder: cellToMerge.placeholder,
    });
  }

  handleKeyDown(
    cell: Compatible<StableTextCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<StableTextCell>; enableEditMode: boolean } {
    const char = getCharFromKeyCode(keyCode, shift);
    if (!ctrl && !alt && isAlphaNumericKey(keyCode) && !(shift && keyCode === keyCodes.SPACE)) {
      return {
        cell: this.getCompatibleCell({
          ...cell,
          originalText: cell.text,
          text: shift ? char : char.toLowerCase(),
        }),
        enableEditMode: true,
      };
    }
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    };
  }

  getClassName(cell: Compatible<StableTextCell>): string {
    const isValid = cell.validator ? cell.validator(cell.text) : true;
    const className = cell.className || '';
    return `${className} ${isValid ? 'valid' : 'invalid'} ${
      cell.placeholder && cell.text === '' ? 'placeholder' : ''
    }`;
  }

  numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  render(
    cell: any,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<StableTextCell>, commit: boolean) => void,
  ): React.ReactNode {
    const format = cell.format?.toLocaleLowerCase();
    const cellText: any = cell?.format ? Number(cell.text) : cell.text;
    const text =
      format === 'number'
        ? `${this.numberWithCommas(cellText?.toFixed(2))}`
        : format === 'integer'
        ? `${this.numberWithCommas(cellText?.toFixed(0))}`
        : format === 'percentage'
        ? `${cellText?.toFixed(1)}%`
        : format === 'currency'
        ? cellText < 0
          ? `(${cell?.currencyType}${this.numberWithCommas(Math.abs(cellText?.toFixed(2)))})`
          : `${cell?.currencyType}${this.numberWithCommas(cellText?.toFixed(2))}`
        : cellText;
    const show = cell?.show;

    if (!isInEditMode) {
      return (
        <div
          style={{
            width: '100%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: '15px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          {cell?.property === 'dimension' ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  pointerEvents: show ? 'auto' : 'none',
                }}
              >
                <div
                  style={{
                    border: `1px solid ${cell.color}`,
                    background: cell.color,
                    height: cell.color ? 20 : 0,
                    width: cell.color ? 20 : 0,
                    marginRight: 8,
                  }}
                />
                <p
                  title={`${cell.text}` || cell.placeholder || ''}
                  style={{
                    paddingLeft: cell?.property === 'name' ? '.2rem' : '0',
                  }}
                >
                  {`${cell.text}` || cell.placeholder || ''}
                </p>
              </div>
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <p
                title={cell.rowId === 'growth_rate' ? `${cell.text}%` : `${text}` || ''}
                style={{
                  paddingLeft: cell?.property === 'name' ? '.2rem' : '0',
                  textAlign: 'center',
                }}
              >
                {cell.rowId === 'growth_rate'
                  ? `${cell.text}%`
                  : cell?.nonEditable
                  ? ''
                  : `${text}` || cell.placeholder || ''}
              </p>
            </div>
          )}
          {cell?.colType === 'header' && 'del'}
        </div>
      );
    }

    return (
      <StableTextEditor
        cell={cell}
        onCellChanged={onCellChanged}
        getCompatibleCell={(nextCell) => this.getCompatibleCell(nextCell)}
      />
    );
  }
}
